import { createSelector } from '@reduxjs/toolkit';
import { DataType } from '@vgtv/api-client/lib/component';

import { selectAssets } from '../assets';
import { selectCategories } from '../categories';
import { selectProfiles } from '../profiles';

import type {
  SvpAssetAdditional,
  SvpAssetId,
} from '@vgtv/api-client/lib/svp_asset';
import type {
  SvpCategoryAdditional,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type { Profile } from '@vgtv/api-client/lib/cnp_tag';
import type { Vendor } from '../../types';
import type { ComponentsState } from './index';

type State = { components: ComponentsState };

const selectAssetsForComponent = createSelector(
  (state: State, vendor: Vendor, componentId: string) =>
    state.components[componentId],
  selectAssets,
  (componentConfig, assets) => {
    if (
      componentConfig?.dataType === DataType.ASSETS ||
      componentConfig?.dataType === DataType.STORIES
    ) {
      const assetIds = componentConfig ? componentConfig.dataIds : [];

      return assetIds
        .map((assetId) => assets[assetId])
        .filter((asset): asset is SvpAssetAdditional => Boolean(asset));
    }

    return [];
  }
);

const selectCategoriesForComponent = createSelector(
  (state: State, vendor: Vendor, componentId: string) =>
    state.components[componentId],
  selectCategories,
  (componentConfig, categories) => {
    if (componentConfig?.dataType !== DataType.CATEGORIES) {
      return [];
    }
    const categoryIds = componentConfig ? componentConfig.dataIds : [];
    return categoryIds
      .map((categoryId) => categories[categoryId])
      .filter((category): category is SvpCategoryAdditional =>
        Boolean(category)
      );
  }
);

const selectProfilesForComponent = createSelector(
  (state: State, componentId: string) => state.components[componentId],
  selectProfiles,
  (componentConfig, profiles) => {
    if (componentConfig?.dataType !== DataType.PROFILES) {
      return [];
    }
    const profilesIds = componentConfig ? componentConfig.dataIds : [];
    return profilesIds
      .map((profileId) => profiles[profileId])
      .filter((profile): profile is Profile => Boolean(profile));
  }
);

const selectMixedForComponent = createSelector(
  (state: State, vendor: Vendor, componentId: string) =>
    state.components[componentId],
  selectAssets,
  selectCategories,
  (componentConfig, assets, categories) => {
    if (componentConfig?.dataType !== DataType.MIXED) {
      return [];
    }
    const uris = componentConfig ? componentConfig.dataIds : [];
    return uris
      .map((uri) => {
        const [type, id] = uri.split(':');

        if (type === 'asset') {
          return {
            type: 'asset' as const,
            id: parseInt(id, 10) as SvpAssetId,
          };
        }

        return {
          type: 'category' as const,
          id: parseInt(id, 10) as SvpCategoryId,
        };
      })
      .map(({ type, id }) => (type === 'asset' ? assets[id] : categories[id]))
      .filter((entity): entity is SvpAssetAdditional | SvpCategoryAdditional =>
        Boolean(entity)
      );
  }
);

const selectComponentHasNextPage = createSelector(
  (state: State, componentId: string) => state.components[componentId],
  (componentConfig) =>
    componentConfig && componentConfig.nextPageParam !== undefined
);

const selectComponentNextPageParam = createSelector(
  (state: State, componentId: string) => state.components[componentId],
  (componentConfig) =>
    componentConfig ? componentConfig.nextPageParam : undefined
);

export {
  selectComponentHasNextPage,
  selectComponentNextPageParam,
  selectAssetsForComponent,
  selectCategoriesForComponent,
  selectProfilesForComponent,
  selectMixedForComponent,
};
